import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';

// API/Components
import axiosInstance from '../../components/axios';
import SingleEmailSettings from '../../components/single_email_settings';
import Toast from '../../components/toast';

// Material UI
import {
	Box,
	FormControlLabel,
	Grid,
	MenuItem,
	Paper,
	Select,
	Toolbar,
	Typography,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Button,
	Switch,
	Tooltip,
} from '@mui/material';

export default function EmailSettings() {

	const { locationNum } = useParams();
	
	const [locationInfo, setLocationInfo] = useState({locationName: ''});
	const [userInfo, setUserInfo] = useState({
		"id": 0,
		"email": "",
		"is_active": false,
		"is_superuser": false,
		"is_staff": false,
		"is_self": true,
	});
	
	const [selectedEmailID, setSelectedEmailID] = useState(0);
	const [emailEntries, setEmailEntries] = useState([]);
	
	const [isLoading, setIsLoading] = useState(true);

	const [allEmailsEnabled, setAllEmailsEnabled] = useState(false);

	const [unsavedChanges, setUnsavedChanges] = useState(false);
	const [openDialog, setOpenDialog] = useState(false);
	const [pendingEmailID, setPendingEmailID] = useState(null);

	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
		clickToClose: true,
		duration: 3000,
	});

	const navigate = useNavigate();

	// Get information about the current user, including permissions info
	useEffect(() => {
		axiosInstance.get(`/users/`).then(response => {
			setUserInfo(response.data);
		})
		.catch(error => {
			console.error('Error fetching user:', error);
		});
	}, []);

	
	// Get information about the location id specified (name, etc.)
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/size/`).then(response => {
			const locationInfo = response.data;
			setLocationInfo({
				locationName: locationInfo.location_name,
				serialNumber: locationInfo.serial_number,
			});
		});
	}, [locationNum]);
	
	// Get the email settings for the location specified
	useEffect(() => {
		getAllEmailSettings();
		getEnableAllEmailsValue()
	}, [locationNum, navigate]);

	// Update the selected email ID when emailSettings change
	useEffect(() => {
		if (emailEntries.length > 0) {
			if (selectedEmailID === 0) {
				setSelectedEmailID(emailEntries[0].id);
			}
		} else {
			setSelectedEmailID(0);
		}
	}, [emailEntries]);

	const getAllEmailSettings = () => {
		setIsLoading(true);

		axiosInstance.get(`/loc/${locationNum}/settings/email/`)
			.then(response => {
				const emailSettings = response.data;
				setEmailEntries(emailSettings);
			})
			.catch(error => {
				console.error('Error fetching email settings:', error);
				if (error.response.status === 403) {
					navigate('/403');
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const getEnableAllEmailsValue = () => {
		axiosInstance.get(`/loc/${locationNum}/settings/email/all`)
			.then(response => {
				let enableAll = response.data;
				if ('email_enable_all' in enableAll) {
					setAllEmailsEnabled(enableAll.email_enable_all == 1);
				}
			})
			.catch(error => {
				console.error('Error fetching Enable All Emails setting:', error);
				setToast({
					open: true,
					severity: 'error',
					message: 'Error fetching Enable All Emails setting: ' + error,
					clickToClose: true,
					duration: 3000,
				});
				setAllEmailsEnabled(false);
			});
	};

	const setEnableAllEmailsValue = (emailsEnabled) => {
		axiosInstance.put(`/loc/${locationNum}/settings/email/all/`, {
				"email_enable_all": emailsEnabled ? 1 : 0,
			})
			.then(response => {
				getEnableAllEmailsValue();

				setToast({
					open: true,
					severity: 'success',
					message: 'Enable All Emails setting saved successfully.',
					clickToClose: true,
					duration: 3000,
				});
			})
			.catch((error) => {
				console.error('Error saving Enable All Emails setting:', error);
				setToast({
					open: true,
					severity: 'error',
					message: 'Error saving Enable All Emails setting: ' + error,
					clickToClose: true,
					duration: 3000,
				});
			});
	};

	const handleChangeSelectedEmailID = (event) => {
		if (unsavedChanges) {
			setPendingEmailID(event.target.value);
			setOpenDialog(true);
		} else {
			setSelectedEmailID(event.target.value);
		}
	};

	const handleChangeEnableAllEmails = (event) => {
		setEnableAllEmailsValue(event.target.checked);
	}

	const handleDialogClose = (confirmLeave) => {
		setOpenDialog(false);
		if (confirmLeave && pendingEmailID !== null) {
			setSelectedEmailID(pendingEmailID);
			setUnsavedChanges(false);
		}
		setPendingEmailID(null);
	};

	function EnableAllSection() {
		let tooltipText = allEmailsEnabled ? 'Emails notifications are currently enabled' : 'All email notifications are currently disabled.';

		return (
			<Paper sx={{ p: 1, my: 2, backgroundColor: '#f9f9f9' }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="body1" marginLeft={1}><strong>Enable All Email Notifications for this Location</strong></Typography>
					<Tooltip title={tooltipText} arrow>
						<Box>
							<FormControlLabel
								control={
									<Switch
										checked={allEmailsEnabled}
										disabled={userInfo.is_superuser === false && userInfo.is_staff === false}
										onChange={(e) => handleChangeEnableAllEmails(e)}
										sx={{ ml: 1 }}
									/>
								}
								label="Enabled"
							/>
						</Box>
					</Tooltip>
				</Box>
			</Paper>
		);
	}
		
	return (
		<>
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
				clickToClose={toast.clickToClose}
				duration={toast.duration}
			/>
			<Paper
				padding='true'
				sx={{
					overflow: 'auto',
					boxShadow: '0',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Typography
					variant='h5'
					fontFamily='Saira, sans-serif'
					fontWeight='600'
					color='#0F1820'
					align='center'
					padding='5px'
				>
					{ locationInfo.locationName } (SN {locationInfo.serialNumber})
				</Typography>
				<Toolbar>
					<Grid container spacing={1} alignItems="center">
						<Grid item xs alignItems="left">
							<Typography
								align='left'
								fontFamily='Saira,sans-serif'
								fontWeight='600'
								sx={{ flexGrow: 1}}
							>
								Email Settings
							</Typography>
						</Grid>
						{ (userInfo.is_superuser === true || userInfo.is_staff === true) &&
							<Grid item minWidth={350}>
								<Select
									value={selectedEmailID}
									onChange={handleChangeSelectedEmailID}
									disabled={userInfo.is_superuser === false && userInfo.is_staff === false}
									fullWidth
									size='small'
								>
									{emailEntries.map((entry) => (
										<MenuItem
											key={entry.id}
											value={entry.id}
											sx={{
												color: entry.enabled ? 'black' : 'gray',
												fontWeight: entry.enabled ? 'bold' : 'normal',
											}}
										>
											{entry.id}. {entry.email_address || '-- empty --'}
										</MenuItem>
									))}
								</Select>
							</Grid>
						}
					</Grid>
				</Toolbar>
				<EnableAllSection />
				<SingleEmailSettings
					locationNum={locationNum}
					emailEntries={emailEntries}
					selectedEmailID={selectedEmailID}
					setUnsavedChanges={setUnsavedChanges}
					getAllEmailSettings={getAllEmailSettings}
					disabled={!allEmailsEnabled}
					userInfo={userInfo}
					loading={isLoading}
				/>
				<Dialog
					open={openDialog}
					onClose={() => handleDialogClose(false)}
				>
					<DialogTitle>Unsaved Changes</DialogTitle>
					<DialogContent>
						<DialogContentText>
							You have unsaved changes. Are you sure you want to leave this page without saving?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => handleDialogClose(false)} color="primary" variant='outlined'>
							Cancel
						</Button>
						<Button onClick={() => handleDialogClose(true)} color="primary" variant='contained' autoFocus>
							Leave
						</Button>
					</DialogActions>
				</Dialog>
			</Paper>
		</>
	);
}